import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { BookabilityDecision, BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import { getBookabilityUnmetCriteriaMessage } from "@src/appV2/Shifts/utils/bookabilityUnmetCriteria";
import { isFuture } from "date-fns";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { BoostedPayLabel } from "../../Pay/BoostedPayLabel";
import { HourlyPayLabel } from "../../Pay/HourlyPayLabel";
import { TotalPayLabel } from "../../Pay/TotalPayLabel";
import {
  SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { InstantPayIndicator } from "../../WorkerShifts/components/InstantPayIndicator";
import { getFormattedShiftPayBreakdownWithShiftAuction } from "../getFormattedShiftPayBreakdown";
import { PlaceBidButton } from "../ShiftAuction/PlaceBidButton";
import { useBidAction } from "../ShiftAuction/useBidAction";
import { BookShiftButton } from "./BookShiftButton";
import { MarkInterestButton } from "./MarkInterestButton";
import { RateNegotiationAction } from "./RateNegotiationAction";
import { RateNegotiationFooter } from "./RateNegotiationFooter";
import { useFooterData } from "./useFooterData";

interface ShiftBottomSheetFooterProps {
  shakeBookabilityCards?: () => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  minutesElapsed?: number;
}

// eslint-disable-next-line complexity
export function ShiftBottomSheetFooter(props: ShiftBottomSheetFooterProps) {
  const { shakeBookabilityCards, scrollContainerRef, minutesElapsed = 0 } = props;

  const useNewRateNegotiationUi = useCbhFlag(CbhFeatureFlag.USE_NEW_RATE_NEGOTIATION_UI, {
    defaultValue: false,
  });

  const {
    isBookable,
    isTestAccount,
    isRateNegotiationEnabled,
    shouldHideBookCta,
    shiftId,
    workplace,
    offer,
    navigateToModal,
    shift,
    isInstantBookDisabled,
    showMarkInterestButton,
    isShiftAlreadyBooked,
    missingBackgroundCheckDataTypes,
  } = useFooterData();

  const { bookabilityStatus, availableShiftBoost } = useShiftModalsDataContext();

  const { showErrorToast } = useToast();
  const bookabilityUnmetCriteria =
    bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.BLOCKED
      ? bookabilityStatus?.attributes.bookability.unmetCriteria
      : [];

  const handleDisabledButtonClick = () => {
    if (bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.BLOCKED) {
      const firstUnmetCriteria = bookabilityUnmetCriteria[0];
      const message = isDefined(shift?.attributes.windowRestriction)
        ? "You don't have access yet. Check back later!"
        : getBookabilityUnmetCriteriaMessage(firstUnmetCriteria);

      scrollContainerRef?.current?.scrollTo({ top: 0, behavior: "smooth" });

      // Show shake animation for missing requirements or when no specific message, otherwise show toast
      if (
        firstUnmetCriteria === BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS ||
        !isDefined(message)
      ) {
        if (shakeBookabilityCards) {
          // wait for scroll-to-top to finish
          setTimeout(() => {
            shakeBookabilityCards();
          }, 500);
        }
      } else {
        showErrorToast(message);
      }
    }
  };

  const { attemptBidAction, isLoading: isBidActionLoading } = useBidAction({
    shiftId,
    workplaceId: workplace?.id,
    navigateToModal,
    onCanProceed: () => {
      if (!isDefined(shiftId)) {
        return;
      }

      navigateToModal(SHIFT_DISCOVERY_SHIFT_AUCTION_PLACE_BID_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: true,
      });
    },
  });

  const handlePlaceBidButtonClick = (shiftId: string) => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_AUCTION_PLACE_BID_BUTTON_CLICKED, { shiftId });
    attemptBidAction();
  };

  const shiftAuction = shift?.shiftAuction;
  const shiftAuctionBid = shift?.shiftAuctionBid;
  const canPlaceBid =
    isDefined(shiftAuction) &&
    bookabilityUnmetCriteria.length === 1 &&
    bookabilityUnmetCriteria[0] === BookabilityUnmetCriteria.SHIFT_AUCTION_ACTIVE &&
    isFuture(shiftAuction.attributes.endTime);

  const handleDisabledPlaceBidButtonClick = () => {
    if (isDefined(shiftAuctionBid)) {
      showErrorToast(
        "You've already placed a bid. You'll be notified whether you've won or not when the auction ends."
      );
    } else {
      handleDisabledButtonClick();
    }
  };

  const shiftAuctionPayBreakdown = shiftAuction
    ? getFormattedShiftPayBreakdownWithShiftAuction(shiftAuction.attributes)
    : undefined;

  const shouldShowRateNegotiation =
    isDefined(shift) &&
    isDefined(offer) &&
    isDefined(workplace?.id) &&
    !isDefined(shiftAuction) &&
    isBookable &&
    isRateNegotiationEnabled;

  if (useNewRateNegotiationUi && shouldShowRateNegotiation) {
    return (
      <RateNegotiationFooter
        offer={offer}
        shift={shift}
        isShiftAlreadyBooked={isShiftAlreadyBooked}
        availableShiftBoost={availableShiftBoost}
        minutesElapsed={minutesElapsed}
        workplace={workplace}
        navigateToModal={navigateToModal}
        isTestAccount={isTestAccount}
        shouldHideBookCta={shouldHideBookCta}
        isInstantBookDisabled={isInstantBookDisabled}
        isBookable={isBookable}
        handleDisabledButtonClick={handleDisabledButtonClick}
        missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
      />
    );
  }

  return (
    <DialogFooter withShadow orientation="vertical">
      {/* TODO: This stack layout will probably not be needed, depending on how metalab will place the rate negotiation button. So this is kind of an wip thing */}
      <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
        <Stack spacing={3} sx={{ paddingLeft: 5 }}>
          {isDefined(shiftAuctionPayBreakdown) && (
            <>
              <HourlyPayLabel size="large" hourlyPay={shiftAuctionPayBreakdown.hourlyPay} />
              {shift?.attributes.isInstantPay && <InstantPayIndicator />}
            </>
          )}
          {isDefined(offer) && !isShiftAlreadyBooked && !isDefined(shiftAuctionPayBreakdown) && (
            <>
              <HourlyPayLabel size="large" hourlyPay={offer.attributes.formattedHourlyPay} />
              <Stack direction="row" spacing={2} alignItems="center">
                {isDefined(availableShiftBoost) ? (
                  <BoostedPayLabel
                    size="medium"
                    totalPayBeforeBoost={offer.attributes.formattedTotalPay}
                    totalWithBoostPay={formatDollarsAsUsd(
                      offer.attributes.totalPay +
                        availableShiftBoost.attributes.amount -
                        (minutesElapsed / 60) * offer.attributes.hourlyPay
                    )}
                  />
                ) : (
                  <TotalPayLabel
                    size="medium"
                    totalPay={formatDollarsAsUsd(
                      offer.attributes.totalPay - (minutesElapsed / 60) * offer.attributes.hourlyPay
                    )}
                  />
                )}
                {shift?.attributes.isInstantPay && <InstantPayIndicator />}
              </Stack>
            </>
          )}
        </Stack>

        {isTestAccount && (
          <Button disabled variant="contained" size="large">
            Test Account
          </Button>
        )}

        {showMarkInterestButton && isDefined(shiftId) && (
          <MarkInterestButton
            shiftId={shiftId}
            workplaceId={workplace?.id ?? ""}
            offerId={offer?.id}
            missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
          />
        )}

        {!isTestAccount &&
          !shouldHideBookCta &&
          isDefined(shift) &&
          isDefined(offer) &&
          !isDefined(shiftAuction) && (
            <BookShiftButton
              isInstantBookDisabled={isInstantBookDisabled}
              disabled={!isBookable}
              shift={shift}
              missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
              rateNegotiationId={undefined}
              onBook={() => {
                navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
                  pathParams: { shiftId: shift.id },
                });
              }}
              onDisabledButtonClick={handleDisabledButtonClick}
            />
          )}
      </Stack>
      {isDefined(shift) && isDefined(shiftAuction) && (
        <Box sx={{ px: 5, width: "100%" }}>
          <PlaceBidButton
            fullWidth
            shiftId={shift.id}
            disabled={!canPlaceBid || isDefined(shiftAuctionBid)}
            isLoading={isBidActionLoading}
            placedBidAmountInMinorUnits={shiftAuctionBid?.attributes.bidAmount.amountInMinorUnits}
            onDisabledButtonClick={handleDisabledPlaceBidButtonClick}
            onPlaceBidButtonClick={handlePlaceBidButtonClick}
          />
        </Box>
      )}

      {isDefined(shift) &&
        isDefined(offer) &&
        isDefined(workplace?.id) &&
        isBookable &&
        isRateNegotiationEnabled && (
          <RateNegotiationAction
            missingBackgroundCheckDataTypes={missingBackgroundCheckDataTypes}
            rateNegotiationStatus={shift.rateNegotiation?.status}
            rateNegotiationLastOfferBy={shift.rateNegotiation?.lastOfferBy}
            rateNegotiationLastOfferPayRate={shift.rateNegotiation?.lastOffer?.hourlyPay}
            shiftId={shift.id}
            workplaceId={workplace.id}
            navigateToModal={navigateToModal}
          />
        )}
    </DialogFooter>
  );
}
